import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogOut, RefreshCw, X } from 'lucide-react';

interface Submission {
    ID: number;
    CreatedAt: string;
    Service: string;
    Data: string; // JSON string
}

import StudentList from './StudentList';

const AdminDashboard = () => {
    const [view, setView] = useState<'submissions' | 'students'>('students');
    const [submissions, setSubmissions] = useState<Submission[]>([]);
    const [loading, setLoading] = useState(true);
    const [selectedSubmission, setSelectedSubmission] = useState<Submission | null>(null);
    const navigate = useNavigate();

    useEffect(() => {
        if (view === 'submissions') fetchSubmissions();
    }, [view]);

    const fetchSubmissions = async () => {
        const token = localStorage.getItem('token');
        if (!token) {
            navigate('/admin/login');
            return;
        }

        try {
            const response = await fetch('/api/admin/submissions', {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (response.status === 401) {
                navigate('/admin/login');
                return;
            }

            const data = await response.json();
            const sorted = Array.isArray(data) ? data.sort((a: any, b: any) => new Date(b.CreatedAt).getTime() - new Date(a.CreatedAt).getTime()) : [];
            setSubmissions(sorted);
        } catch (err) {
            console.error("Failed to fetch submissions", err);
        } finally {
            setLoading(false);
        }
    };

    const handleLogout = () => {
        localStorage.removeItem('token');
        localStorage.removeItem('role');
        navigate('/admin/login');
    };

    const parseData = (jsonStr: string) => {
        try {
            const data = JSON.parse(jsonStr);
            const phone = data.phone || data.Phone || '';
            return phone ? `• ${phone}` : '';
        } catch {
            return "";
        }
    };

    const renderDetails = (jsonStr: string) => {
        try {
            const data = JSON.parse(jsonStr);
            return (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {Object.entries(data).map(([key, value]) => (
                        <div key={key} className="bg-[#F8FAFC] p-4 rounded-xl border border-slate-100">
                            <h4 className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1.5">
                                {key.replace(/_/g, ' ')}
                            </h4>
                            <p className="text-[#011E26] font-semibold text-sm break-words">
                                {Array.isArray(value) ? value.join(', ') : String(value)}
                            </p>
                        </div>
                    ))}
                </div>
            );
        } catch {
            return <p className="text-red-500">Error parsing data</p>;
        }
    };

    return (
        <div className="min-h-screen bg-[#F1F5F9]">
            {/* Header */}
            <div className="bg-white border-b border-slate-200 px-6 sticky top-0 z-20">
                <div className="max-w-7xl mx-auto flex justify-between items-center">
                    <div className="flex items-center gap-8">
                        {/* Logo + Title */}
                        <div className="flex items-center gap-3 py-4">
                            <img
                                src="/assets/logo.png"
                                alt="NexAI"
                                className="h-8 w-auto object-contain"
                            />
                            <span className="text-base font-bold text-[#011E26]">Admin Dashboard</span>
                        </div>

                        {/* Tab Navigation */}
                        <nav className="hidden md:flex items-center gap-0 h-full">
                            <button
                                onClick={() => setView('students')}
                                className={`relative px-5 py-4 text-sm font-semibold transition-colors ${
                                    view === 'students'
                                        ? 'text-[#011E26]'
                                        : 'text-slate-400 hover:text-slate-600'
                                }`}
                            >
                                Students
                                {view === 'students' && (
                                    <span className="absolute bottom-0 left-0 right-0 h-[2.5px] bg-[#009B8F] rounded-full" />
                                )}
                            </button>
                            <button
                                onClick={() => setView('submissions')}
                                className={`relative px-5 py-4 text-sm font-semibold transition-colors ${
                                    view === 'submissions'
                                        ? 'text-[#011E26]'
                                        : 'text-slate-400 hover:text-slate-600'
                                }`}
                            >
                                Inquiries
                                {view === 'submissions' && (
                                    <span className="absolute bottom-0 left-0 right-0 h-[2.5px] bg-[#009B8F] rounded-full" />
                                )}
                            </button>
                        </nav>
                    </div>

                    <div className="flex items-center gap-3">
                        {view === 'submissions' && (
                            <button onClick={fetchSubmissions} className="p-2 text-slate-400 hover:text-[#009B8F] transition-colors rounded-lg hover:bg-slate-50" title="Refresh">
                                <RefreshCw size={18} />
                            </button>
                        )}
                        <button
                            onClick={handleLogout}
                            className="flex items-center gap-2 px-4 py-2 border border-slate-200 rounded-lg text-slate-600 hover:bg-slate-50 hover:border-slate-300 text-sm font-medium transition-all"
                        >
                            <LogOut size={15} /> Logout
                        </button>
                    </div>
                </div>
            </div>

            {/* Content */}
            <div className="max-w-7xl mx-auto p-6 md:p-8">
                {view === 'students' ? (
                    <StudentList />
                ) : (
                    <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
                        <div className="px-8 py-6 border-b border-slate-100">
                            <h2 className="text-lg font-bold text-[#011E26]">Recent Inquiries</h2>
                        </div>

                        {loading ? (
                            <div className="p-16 text-center text-slate-400 font-medium">Loading data...</div>
                        ) : submissions.length === 0 ? (
                            <div className="p-16 text-center text-slate-400">No submissions found.</div>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="w-full text-left text-sm">
                                    <thead>
                                        <tr className="text-[10px] font-bold text-slate-400 uppercase tracking-widest bg-slate-50/60 border-b border-slate-100">
                                            <th className="px-8 py-4">ID</th>
                                            <th className="px-8 py-4">Date</th>
                                            <th className="px-8 py-4">Service</th>
                                            <th className="px-8 py-4">Summary</th>
                                            <th className="px-8 py-4">Status</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-50">
                                        {submissions.map((sub) => (
                                            <tr
                                                key={sub.ID}
                                                onClick={() => setSelectedSubmission(sub)}
                                                className="hover:bg-slate-50/50 transition-colors cursor-pointer group"
                                            >
                                                <td className="px-8 py-5 font-mono text-sm text-slate-500 font-medium">#{sub.ID}</td>
                                                <td className="px-8 py-5 text-slate-700 font-medium text-sm">
                                                    {new Date(sub.CreatedAt).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                                                    <span className="text-xs text-slate-400 ml-1.5">
                                                        {new Date(sub.CreatedAt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                                                    </span>
                                                </td>
                                                <td className="px-8 py-5">
                                                    <span className="inline-block px-3 py-1 bg-[#009B8F]/10 text-[#009B8F] rounded-full text-[10px] font-bold uppercase tracking-wider border border-[#009B8F]/20">
                                                        {sub.Service}
                                                    </span>
                                                </td>
                                                <td className="px-8 py-5 text-slate-600 font-medium text-sm">
                                                    {parseData(sub.Data)}
                                                </td>
                                                <td className="px-8 py-5">
                                                    <span className="inline-block px-3 py-1 bg-[#009B8F]/10 text-[#009B8F] rounded-full text-[10px] font-bold uppercase tracking-wider border border-[#009B8F]/20">
                                                        New
                                                    </span>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Detail Modal */}
            {selectedSubmission && (
                <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4 z-50" onClick={() => setSelectedSubmission(null)}>
                    <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()}>
                        <div className="p-6 border-b border-slate-100 flex justify-between items-start sticky top-0 bg-white rounded-t-2xl">
                            <div>
                                <h3 className="text-xl font-bold text-[#011E26]">Submission Details</h3>
                                <p className="text-slate-400 text-sm mt-0.5">ID: #{selectedSubmission.ID} &bull; {selectedSubmission.Service}</p>
                            </div>
                            <button
                                onClick={() => setSelectedSubmission(null)}
                                className="p-1.5 hover:bg-slate-100 rounded-lg transition-colors text-slate-400 hover:text-slate-600"
                            >
                                <X size={20} />
                            </button>
                        </div>
                        <div className="p-6">
                            {renderDetails(selectedSubmission.Data)}
                        </div>
                        <div className="p-6 border-t border-slate-100 flex justify-end">
                            <button
                                onClick={() => setSelectedSubmission(null)}
                                className="px-6 py-2.5 bg-[#009B8F] text-white font-semibold rounded-xl hover:bg-[#00857a] transition-colors text-sm"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminDashboard;
